//// Application Packages
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import axios from 'axios'
import { object, string } from 'yup'
import { translateField } from '../../functions/field-functions'
import { extractSubmissionPage } from '../../functions/page-functions'

//// Application Slices
import { selectSettings } from '../../reducers/settingSlice'
import {
    updateAccountData,
    updateAccountValidity,
    deleteAccountData,
    selectAccount,
} from '../../reducers/accountSlice'

//// Application Components
import DividerComponent from '../../components/simple-components/divider-component'
import ButtonComponent from '../../components/conditional-components/button-component'
import LoadingComponent from '../../components/simple-components/loading-component'
import NotificationComponent from '../../components/conditional-components/notification-component'
import ModalComponent from '../../components/conditional-components/modal-component'

//// Carbon Components
import { Form, TextInput } from '@carbon/react'

//// Carbon Icons

//// Static Constants
const successMessage = () => {
    return `
<p>
Your account user id will be emailed to you shortly.
</p>
`
}

//////// Component
const ForgotUserIdPage = () => {
    //////// Hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()

    //////// State
    const [loading, setLoading] = useState(false)
    const [notification, setNotification] = useState({})
    const [modalData, setModalData] = useState({})
    const [modalOpen, setModalOpen] = useState(false)

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const accountData = useSelector(selectAccount)

    //// Slice Data
    const digital_name = settingsData?.['page']?.['digital_name']
    const written_name = settingsData?.['page']?.['written_name']
    const submit_route = settingsData?.['page']?.['submit_route']
    const fields = settingsData?.['page']?.['fields']

    //////// Search Params

    //////// Methods

    // Orchestrate Forgot User Id Data (Integration)
    const orchestrateForgotUserId = async (accountData) => {
        try {
            if (accountData) {
                const base_url = `${process.env.REACT_APP_BASE_URL}`
                const api_endpoint = `${process.env.REACT_APP_ORCHESTRATION_ENDPOINT_PROXY_ACCOUNT_FORGOT_USER_ID}`
                const url = base_url + api_endpoint
                const { Email } = accountData

                // Create Request
                if (Email) {
                    const { data } = await axios.post(url, {
                        email: Email,
                    })
                    if (data) {
                        return data
                    } else {
                        console.log(
                            `Account Forgot User Id: Response Is Undefined`
                        )
                        return null
                    }
                }
            } else {
                console.log(`Account Forgot User Id: Request Is Undefined`)
                return null
            }
        } catch (error) {
            console.warn('Orchestrate Forgot User Id Error:', error)
            return null
        }
    }

    // Re-Validate Data (Utility)
    const revalidation = async (event_name) => {
        try {
            // Update Validation State
            if (digital_name && event_name) {
                dispatch(
                    updateAccountValidity({
                        digital_name: digital_name,
                        errors: {
                            [event_name]: false,
                            [event_name + 'ErrMsg']: '',
                        },
                    })
                )
            }
        } catch (error) {
            console.warn('Revalidation Error:', error)
            return null
        }
    }

    // Validate Data (Check)
    const validation = async (submit) => {
        // Validate Form Submission Data Against Schema
        try {
            // Schema Object
            const schema = {}
            schema.yup = object({
                Email: string()
                    .email()
                    .min(0)
                    .max(50)
                    .required(
                        'This field is required to be entered or selected.'
                    ),
            })
            schema.valid = {
                Email: false,
                EmailErrMsg: '',
            }
            await schema.yup.validate(
                { ...submit },
                { strict: true, abortEarly: false }
            )
            dispatch(
                updateAccountValidity({
                    digital_name: digital_name,
                    errors: {},
                })
            )
            return true
        } catch (err) {
            const errors = {}
            if (err?.inner?.length > 0) {
                await err.inner.forEach(async (el) => {
                    if (el?.path) {
                        errors[el?.path] = true
                        if (el?.errors?.length > 0) {
                            const errName = el?.path + 'ErrMsg'
                            const errMsg = el?.errors?.join(' ')
                            const tran = translateField(fields, el?.path)
                            if (tran) {
                                const tranErrMsg = await errMsg.replaceAll(
                                    el?.path,
                                    tran
                                )
                                errors[errName] = tranErrMsg
                            } else {
                                errors[errName] = el?.errors?.join(' ')
                            }
                        }
                    }
                })
            }
            //// Start Watson Assistant Journey

            //// End Watson Assistant Journey
            dispatch(
                updateAccountValidity({
                    digital_name: digital_name,
                    errors: errors,
                })
            )
            return false
        }
    }

    //////// Functional Methods

    // Handle Data (Method)
    const handleData = (e) => {
        if (e?.target?.name && e?.target?.value) {
            dispatch(
                updateAccountData({
                    digital_name: digital_name,
                    values: {
                        [e.target.name]: e.target.value,
                    },
                })
            )
            revalidation(e.target.name)
        } else if (e?.target?.name) {
            dispatch(
                updateAccountData({
                    digital_name: digital_name,
                    values: { [e.target.name]: '' },
                })
            )
            revalidation(e.target.name)
        }
        //// Start Watson Assistant Journey

        //// End Watson Assistant Journey
    }

    // Handle Clear Data (Hard Reset)
    const handleClearData = () => {
        //// Perform Browser Effects

        localStorage.clear()
        sessionStorage.clear()

        //// Perform Data Clearing

        dispatch(
            updateAccountData({
                digital_name: digital_name,
                values: {
                    TemporaryPassword: '',
                    NewPassword: '',
                    NewPasswordConfirm: '',
                },
            })
        )

        dispatch(
            deleteAccountData({
                digital_name: digital_name,
            })
        )

        //// End Data Clearing

        //// Perform Browser Effects

        console.clear()
        window.location.reload()

        //// End Perform Browser Effects
    }

    // Handle Submission (Method)
    const handleSubmit = async () => {
        try {
            // Extract Account Data From Redux
            const submit = await extractSubmissionPage(
                digital_name,
                accountData
            )

            // Validate Account Submission Data
            const valid = await validation(submit)

            // Check If Account Data Is Valid
            if (valid) {
                console.info('Account Forgot User Id: Passed Valid')

                setLoading(true)

                // Send Orchestration Request
                const orchestrate = await orchestrateForgotUserId(submit)

                // Check If Orchestration Processed Submission And Sent Email
                if (
                    orchestrate?.account_exists &&
                    orchestrate?.forgot_user_id_email_sent
                ) {
                    console.info('Account Forgot User Id: Succcess')

                    setLoading(false)
                    setModalOpen(true)
                    setModalData({
                        open: true,
                        alert: true,
                        label: 'Account Forgot User Id',
                        heading: 'Successfully Found Your User Id',
                        paragraph: successMessage(),
                        submit_label: 'Login',
                        submit_route: submit_route,
                        around_close: true,
                    })
                    setNotification({
                        type: 'inline',
                        visible: false,
                        kind: '',
                        title: '',
                        subtitle: '',
                    })

                    // Clear Existing Data
                    dispatch(
                        deleteAccountData({
                            digital_name: digital_name,
                        })
                    )
                } else if (
                    orchestrate?.account_exists &&
                    !orchestrate?.forgot_user_id_email_sent
                ) {
                    console.info('Account Forgot User Id: Succcess')
                    console.info('Account Forgot User Id: Failed To Send Email')

                    setLoading(false)
                    setNotification({
                        type: 'inline',
                        visible: true,
                        kind: 'error',
                        title: 'Account Forgot User Id Failed:',
                        subtitle:
                            'Failed To Send Email, Please Try Again Or Contact Support',
                    })
                } else {
                    console.info('Account Forgot User Id: Failed')

                    setLoading(false)
                    setNotification({
                        type: 'inline',
                        visible: true,
                        kind: 'error',
                        title: 'Account Forgot User Id Failed:',
                        subtitle: 'Account Email Address Invalid',
                    })

                    console.log(
                        'Account Forgot User Id Failed: Account Email Address Invalid'
                    )
                }
            } else {
                console.info('Account Forgot User Id: Failed')

                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: true,
                    kind: 'error',
                    title: 'Account Forgot User Id Failed:',
                    subtitle: 'Account Email Address Invalid',
                })

                console.log(
                    'Account Forgot User Id Failed: Account Email Address Invalid'
                )
            }
        } catch (error) {
            console.warn('Handle Submit Error:', error)
            return null
        }
    }

    // Handle Modal Submit (Method)
    const handleModalSubmit = async () => {
        try {
            // Navigate To Next Form
            navigate(submit_route ? submit_route : '/')

            // Clear Existing Data
            handleClearData()
        } catch (error) {
            console.warn('Handle Modal Submit Error:', error)
            return null
        }
    }

    // Handle Modal Close (Method)
    const handleModalClose = async () => {
        try {
            setModalOpen(false)

            // Clear Existing Data
            handleClearData()
        } catch (error) {
            console.warn('Handle Modal Close Error:', error)
            return null
        }
    }

    // Handle Keyboard Enter As Submit (Method)
    const handleKeyDown = async (event) => {
        try {
            if (event?.key === 'Enter') {
                await handleSubmit()
            }
        } catch (error) {
            console.warn('Handle Key Down Error:', error)
            return null
        }
    }

    //////// Memoization

    //////// Effects/Side Effects

    //////////////// Render & Return
    if (loading) {
        return (
            <>
                <div className="form" data-testid="account-registration-page">
                    <h2
                        className="form-title"
                        title="page-heading"
                        role="heading"
                        aria-level="1"
                    >
                        {written_name ? written_name : ``}
                    </h2>

                    <LoadingComponent />
                </div>
            </>
        )
    } else {
        return (
            <>
                <div
                    className="form"
                    data-testid="account-registration-page"
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                >
                    <h2
                        className="form-title"
                        title="page-heading"
                        role="heading"
                        aria-level="1"
                    >
                        {written_name ? written_name : ``}
                    </h2>

                    <Form onSubmit={handleSubmit}>
                        <DividerComponent text="Account Credentials" />

                        <TextInput
                            className="required"
                            labelText="Email:"
                            id="Email"
                            name="Email"
                            autocomplete="email"
                            placeholder="email@email.com"
                            value={accountData?.[digital_name]?.values?.Email}
                            maxLength="50"
                            type="email"
                            onChange={handleData}
                            invalid={accountData?.[digital_name]?.errors?.Email}
                            invalidText={
                                accountData?.[digital_name]?.errors?.EmailErrMsg
                            }
                        />

                        <ModalComponent
                            modal={modalData}
                            open={modalOpen}
                            handleModalClose={handleModalClose}
                            handleModalSubmit={handleModalSubmit}
                        />

                        <NotificationComponent notification={notification} />

                        <div className="button-group">
                            <div className="button-group-item">
                                <ButtonComponent
                                    click={handleSubmit}
                                    title="Submit"
                                />
                            </div>
                        </div>
                    </Form>
                </div>

                <span
                    className="visually-hidden"
                    data-testid="account-forgot-user-id-page-errors"
                    role="log"
                    aria-disabled="true"
                >
                    {JSON.stringify(accountData?.[digital_name]?.errors)}
                </span>
            </>
        )
    }
}

export default ForgotUserIdPage
